import React, { useState } from 'react';
import { Button, Form, Modal, ModalProps } from 'react-bootstrap';
import {
	EmojiFrown,
	EmojiFrownFill,
	EmojiNeutral,
	EmojiNeutralFill,
	EmojiSmile,
	EmojiSmileFill,
} from 'react-bootstrap-icons';
import { FEEDBACK_RATING_ID } from '@/lib/models';
import { aiConversationService } from '@/lib/services';
import { createUseThemedStyles, useHandleError } from '@/hooks';
import { FadeTransition, LoadingButton } from '@/components';

const useStyles = createUseThemedStyles({
	dialog: {
		width: '90%',
		height: '100%',
		maxWidth: 610,
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			overflowY: 'auto',
		},
	},
	radioButtonLabel: {
		'&:after, &:before': {
			display: 'none !important',
		},
	},
});

interface AiRateModalProps extends ModalProps {
	conversationQueryId?: string;
	onSave(): void;
}

const satisfactionOptions = [
	{
		value: FEEDBACK_RATING_ID.POSITIVE,
		checkedIcon: <EmojiSmileFill size={116} className="text-success" />,
		uncheckedIcon: <EmojiSmile size={116} className="text-success" />,
	},
	{
		value: FEEDBACK_RATING_ID.NEUTRAL,
		checkedIcon: <EmojiNeutralFill size={116} className="text-warning" />,
		uncheckedIcon: <EmojiNeutral size={116} className="text-warning" />,
	},
	{
		value: FEEDBACK_RATING_ID.NEGATIVE,
		checkedIcon: <EmojiFrownFill size={116} className="text-danger" />,
		uncheckedIcon: <EmojiFrown size={116} className="text-danger" />,
	},
];

export const AiRateModal = ({ conversationQueryId, onSave, ...props }: AiRateModalProps) => {
	const handleError = useHandleError();
	const classes = useStyles();
	const [formValues, setFormValues] = useState({
		satisfaction: undefined as FEEDBACK_RATING_ID | undefined,
		reason: '',
	});
	const [isSaving, setIsSaving] = useState(false);

	const handleOnEnter = () => {
		setIsSaving(false);
		setFormValues({
			satisfaction: undefined,
			reason: '',
		});
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		try {
			if (!conversationQueryId) {
				throw new Error('conversationQueryId is undefined.');
			}

			if (!formValues.satisfaction) {
				throw new Error('Feedback is undefined.');
			}

			setIsSaving(true);

			await aiConversationService
				.rateConversationQuery({
					conversationQueryId,
					feedbackRatingId: formValues.satisfaction,
					...(formValues.reason && { reason: formValues.reason }),
				})
				.fetch();

			onSave();
		} catch (error) {
			handleError(error);
			setIsSaving(false);
		}
	};

	return (
		<Modal dialogClassName={classes.dialog} centered onEntering={handleOnEnter} {...props}>
			<Modal.Header closeButton>
				<Modal.Title>AskHackett&trade; Response</Modal.Title>
			</Modal.Header>
			<Form onSubmit={handleFormSubmit}>
				<fieldset disabled={isSaving}>
					<Modal.Body>
						<p className="mb-6">How satisfied were you with the response provided by AskHackett&trade;?</p>
						<Form.Group className="d-flex align-items-center justify-content-around">
							{satisfactionOptions.map((option) => (
								<Form.Check id={`satisfaction--${option.value}`}>
									<Form.Check.Input
										type="radio"
										name="satisfaction"
										value={option.value}
										checked={formValues.satisfaction === option.value}
										onChange={({ currentTarget }) => {
											setFormValues((previousValue) => ({
												...previousValue,
												satisfaction: currentTarget.value as FEEDBACK_RATING_ID,
											}));
										}}
									/>
									<Form.Check.Label className={classes.radioButtonLabel}>
										{formValues.satisfaction === option.value
											? option.checkedIcon
											: option.uncheckedIcon}
									</Form.Check.Label>
								</Form.Check>
							))}
						</Form.Group>
						<FadeTransition in={!!formValues.satisfaction}>
							<Form.Group className="pt-4">
								{formValues.satisfaction === FEEDBACK_RATING_ID.POSITIVE && (
									<Form.Label>Tell us more about why you were satisfied (Optional)</Form.Label>
								)}
								{formValues.satisfaction === FEEDBACK_RATING_ID.NEUTRAL && (
									<Form.Label>
										Tell us more about how the response could have been improved (Optional)
									</Form.Label>
								)}
								{formValues.satisfaction === FEEDBACK_RATING_ID.NEGATIVE && (
									<Form.Label>Tell us more about why you were dissatisfied</Form.Label>
								)}
								<Form.Control
									type="text"
									placeholder="Enter text..."
									value={formValues.reason}
									onChange={({ currentTarget }) => {
										setFormValues((previousValue) => ({
											...previousValue,
											reason: currentTarget.value,
										}));
									}}
									required={formValues.satisfaction === FEEDBACK_RATING_ID.NEGATIVE}
								/>
							</Form.Group>
						</FadeTransition>
					</Modal.Body>
					<Modal.Footer>
						<div className="text-right">
							<Button className="mr-2" variant="link" onClick={props.onHide}>
								Cancel
							</Button>
							<LoadingButton type="submit" isLoading={isSaving} disabled={!formValues.satisfaction}>
								Submit
							</LoadingButton>
						</div>
					</Modal.Footer>
				</fieldset>
			</Form>
		</Modal>
	);
};
